import type {
  BuildMarker,
  GameDevStage,
  PlaytestSessionState,
} from '@playerbase/types';

import type { SessionListFilters } from '../../games/playtest-sessions-data.js';

import { BuildPicker } from './build-picker.js';

interface Props {
  value: SessionListFilters;
  onChange: (next: SessionListFilters) => void;
  builds: BuildMarker[];
  stages: GameDevStage[];
}

const STATE_CHIPS: Array<{ label: string; value: PlaytestSessionState | null }> = [
  { label: 'Any', value: null },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' },
  { label: 'Expired', value: 'expired' },
];

// Filter chips + text inputs for the session list. Applies in real time
// (no Apply button); the parent hook fires a fresh fetch each time the
// filters object identity changes.
export function SessionsFilterBar({ value, onChange, builds, stages }: Props): JSX.Element {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-surface-2 px-3 py-2">
      <div className="flex flex-col gap-1">
        <label
          htmlFor="session-filter-tester"
          className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted"
        >
          Tester
        </label>
        <input
          id="session-filter-tester"
          type="text"
          value={value.tester ?? ''}
          onChange={(e) =>
            onChange({ ...value, tester: e.target.value === '' ? null : e.target.value })
          }
          placeholder="handle or id"
          className="rounded-md border border-border bg-surface px-2 py-1 text-sm text-text"
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          Build
        </span>
        <BuildPicker
          value={value.buildMarkerId}
          onChange={(next) => onChange({ ...value, buildMarkerId: next })}
          builds={builds}
          stages={stages}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor="session-filter-started"
          className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted"
        >
          Started after
        </label>
        <input
          id="session-filter-started"
          type="date"
          value={value.startedAfter ?? ''}
          onChange={(e) =>
            onChange({
              ...value,
              startedAfter: e.target.value === '' ? null : e.target.value,
            })
          }
          className="rounded-md border border-border bg-surface px-2 py-1 text-sm text-text"
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          State
        </span>
        <div className="flex gap-1">
          {STATE_CHIPS.map((chip) => {
            const active = value.state === chip.value;
            return (
              <button
                key={String(chip.value)}
                type="button"
                onClick={() => onChange({ ...value, state: chip.value })}
                className={
                  active
                    ? 'rounded-full border border-accent/60 bg-accent/10 px-3 py-1 text-xs text-text'
                    : 'rounded-full border border-border bg-surface px-3 py-1 text-xs text-text-muted hover:text-text'
                }
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
